import { BlazonHexagonBorder } from 'app/common/blazon/hexagon-border/BlazonHexagonBorder'
import React, { useEffect, useMemo, useState } from 'react'
import {
    breakpoints,
    media,
    ResponsiveContainer,
    style
} from 'app/assets/styles'
import { ModalTitle } from 'app/common/modals/title'
import { FormattedMessage } from 'react-intl'
import Modal from 'react-modal'
import { H3, SmallText, Text } from 'app/common/typography'
import { colors } from 'shared/assets/styles'
import { Picture } from 'react-responsive-picture'
import styled from 'styled-components'
import { getQrCode } from 'app/common/utils'
import { Collapse } from 'react-collapse'
import { Chevron } from 'app/assets/icons'
import { useSelector } from 'react-redux'
import type { RootState } from 'shared/store/reducers'
import { FeatureToggle } from 'app/common/feature-toggle'
import { CcCartCheck } from 'app/ecommerce/click-and-collect/cart-check'
import { featureCodes } from 'shared/utils'
import { IconButton } from 'app-v2/design-systems/components/inputs/button'
import { Icon } from 'app-v2/shared/features/icon'
import { tokens } from 'app-v2/shared/features/icon/utils/tokens'

const { shadow, text, yellow } = colors
const { classicModalMobileStyle, classicModalStyle } = style

type Props = {
    endDate: string,
    id: string,
    title: string,
    isOpen: boolean,
    legalMentions: string,
    restaurants: Array<string>,
    handleClose: () => void,
    className?: string,
    blazonProfileImageUrl?: string,
    imageUrl?: string
}

const ZoomModal = (props: Props) => {
    const {
        id,
        title,
        restaurants,
        legalMentions,
        endDate,
        isOpen,
        handleClose,
        className,
        blazonProfileImageUrl,
        imageUrl = ''
    } = props

    const [qrCodeImage, setQrCodeImage] = useState()
    const [legalMentionsCollapsed, setLegalMentionsCollapsed] = useState(false)
    const [restaurantCollapsed, setRestaurantCollapsed] = useState(false)
    const [blazonsCollapsed, setBlazonsCollapsed] = useState(false)
    const [width, setWidth] = useState(window.innerWidth)

    useEffect(() => {
        getQrCode(id).then(r => setQrCodeImage(r))
    }, [])

    useEffect(() => {
        const handleResize = () => {
            setWidth(window.innerWidth)
        }
        window.addEventListener('resize', handleResize)
        handleResize()
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    const { coupons } = useSelector(
        (state: RootState) => state.kingdomPrivilegesPageState
    )
    const isActiveInRestaurant = useMemo(
        //$FlowFixMe
        () => coupons?.find(c => c.id === id)?.isActiveInRestaurant ?? true,
        [coupons, id]
    )

    const isActiveInDelivery = useMemo(() => {
        const coupon = coupons && coupons.find(c => c.id === id)
        if (!coupon) {
            return false
        }
        return coupon.canals.includes('DELIVERY_IN_APP')
    }, [coupons, id])
    const isActiveinClickAndCollect = useMemo(() => {
        const coupon = coupons && coupons.find(c => c.id === id)
        if (!coupon) {
            return false
        }
        return (
            coupon.canals.includes('PICK_UP') ||
            coupon.canals.includes('PARKING') ||
            coupon.canals.includes('DRIVE') ||
            coupon.canals.includes('TABLE_SERVICE') ||
            coupon.canals.includes('ON_SITE')
        )
    }, [coupons, id])

    const mobile = width < breakpoints.medium - 1

    const renderRestaurants = () => {
        return (
            restaurants.length > 0 && (
                <div>
                    <CollapseTitle
                        onClick={e => {
                            e.stopPropagation()
                            setRestaurantCollapsed(!restaurantCollapsed)
                        }}>
                        <SmallText>
                            <FormattedMessage id="kingdom.privileges.coupon.modal.zoom.restaurants" />
                        </SmallText>
                        <Chevron
                            color={text}
                            direction={restaurantCollapsed ? 'up' : 'down'}
                        />
                    </CollapseTitle>
                    <Collapse isOpened={restaurantCollapsed}>
                        <RestaurantsContainer>
                            {restaurants.map(
                                (restaurant: string, index: number) => (
                                    <Restaurant key={index}>
                                        {restaurant}
                                    </Restaurant>
                                )
                            )}
                        </RestaurantsContainer>
                    </Collapse>
                    <Line />
                </div>
            )
        )
    }

    const renderLegalMentions = () => {
        return (
            <div>
                <Line />
                <CollapseTitle
                    onClick={e => {
                        e.stopPropagation()
                        setLegalMentionsCollapsed(!legalMentionsCollapsed)
                    }}>
                    <FormattedMessage id="see.legal.mentions">
                        {(text: string) => <SmallText>{text}</SmallText>}
                    </FormattedMessage>
                    <Chevron
                        color={text}
                        direction={legalMentionsCollapsed ? 'up' : 'down'}
                    />
                </CollapseTitle>
                <Collapse isOpened={legalMentionsCollapsed}>
                    <Collapsible>{legalMentions}</Collapsible>
                </Collapse>
                <Line />
            </div>
        )
    }

    const renderBlazons = () => {
        return (
            <div>
                <CollapseTitle
                    onClick={e => {
                        e.stopPropagation()
                        setBlazonsCollapsed(!blazonsCollapsed)
                    }}>
                    <FormattedMessage id="kingdom.challenge.coupon.associated.blazon">
                        {(text: string) => <SmallText>{text}</SmallText>}
                    </FormattedMessage>
                    <Chevron
                        color={text}
                        direction={blazonsCollapsed ? 'up' : 'down'}
                    />
                </CollapseTitle>
                <Collapse isOpened={blazonsCollapsed}>
                    <Collapsible>
                        <BlazonContainer>
                            {/* $FlowFixMe */}
                            <BlazonHexagonBorder src={blazonProfileImageUrl} />
                        </BlazonContainer>
                    </Collapsible>
                </Collapse>
                <Line />
            </div>
        )
    }

    return (
        <Modal
            ariaHideApp={false}
            isOpen={isOpen}
            shouldCloseOnOverlayClick
            onRequestClose={handleClose}
            style={mobile ? classicModalMobileStyle : classicModalStyle}>
            <StyledResponsiveContainer>
                <ModalTitle
                    center
                    handleClose={handleClose}
                    titleNode={
                        <H3 color={yellow} center>
                            {title}
                        </H3>
                    }
                    className={className}
                />
                {endDate && (
                    <Infos>
                        <FormattedMessage
                            id="kingdom.privileges.coupon.validity.label"
                            values={{ date: endDate }}>
                            {(label: string) => (
                                <StyledText center>{label}</StyledText>
                            )}
                        </FormattedMessage>
                    </Infos>
                )}
                <Content>
                    {qrCodeImage && isActiveInRestaurant && (
                        <QRCodeContainer pointerEvents="none">
                            <Image
                                sources={[
                                    {
                                        srcSet: [qrCodeImage]
                                    }
                                ]}
                            />
                        </QRCodeContainer>
                    )}
                    {!isActiveInRestaurant && imageUrl && (
                        <ImageContainer>
                            <SmallImage
                                sources={[
                                    {
                                        srcSet: [imageUrl]
                                    }
                                ]}
                            />
                        </ImageContainer>
                    )}
                    <Infos>
                        {isActiveInRestaurant && (
                            <FormattedMessage
                                id="kingdom.privileges.coupon.modal.zoom.code"
                                values={{ id }}>
                                {(text: string) => <H3 center>{text}</H3>}
                            </FormattedMessage>
                        )}
                    </Infos>
                    {isActiveinClickAndCollect && (
                        <CcButtonContainer>
                            <FeatureToggle
                                feature={featureCodes.CLICK_AND_COLLECT}
                                key={0}>
                                <CcCartCheck
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}>
                                    <IconButton
                                        icon={
                                            <Icon
                                                code={tokens.ccHand}
                                                size={25}
                                                marginRight={5}
                                                color={colors.mayoEggWhite}
                                            />
                                        }>
                                        Click & collect
                                    </IconButton>
                                </CcCartCheck>
                            </FeatureToggle>
                        </CcButtonContainer>
                    )}
                    <CollapsibleContainer>
                        {renderLegalMentions()}
                        {renderRestaurants()}
                        {!!blazonProfileImageUrl && renderBlazons()}
                    </CollapsibleContainer>
                </Content>
            </StyledResponsiveContainer>
        </Modal>
    )
}

const StyledResponsiveContainer = styled(ResponsiveContainer)`
    ${media.large`
        margin: 0 20px;
        width: auto;
    `};
`

const Content = styled.div`
    height: 80vh;
    margin: 20px 0;
`

const QRCodeContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 50%;
`

const Image = styled(Picture)`
    width: 200%;
`

const ImageContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`

const SmallImage = styled(Picture)`
    width: 100%;
`

const StyledText = styled(Text)`
    margin: 10px 0;
`

const Infos = styled.div`
    margin-bottom: 20px;
    text-align: center;
`

const CollapsibleContainer = styled.div`
    margin: 20px 0;
    .ReactCollapse--collapse {
        transition: height 500ms;
    }
`

const CollapseTitle = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin: 10px 5px;
`

const Line = styled.div`
    width: 100%;
    height: 1px;
    background: ${shadow};
`

const Collapsible = styled(SmallText)`
    margin-bottom: 10px;
`

const RestaurantsContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin-bottom: 10px;
`

const Restaurant = styled(SmallText)`
    margin-left: 30px;
`

const BlazonContainer = styled.div`
    width: 90px;
    height: 90px;
    margin-bottom: 10px;
`

const CcButtonContainer = styled.div`
    display: flex;
    align-content: center;
    justify-content: center;
    margin-left: 15px;
`

export { ZoomModal }
